/**
 * Spatial Analysis Advisor
 *
 * Consumes parsed intent JSON from the DIP and walks the decision graph in
 * spatial-analysis-knowledge.json to recommend the optimal visualization
 * method, classification scheme, color ramp type, and layer type.
 *
 * Pure function, no side effects. Advisory only — returns an enrichment
 * object that downstream op handlers and the classification engine can
 * optionally consume. Never blocks the pipeline.
 *
 * Source-of-truth textbooks are merged in spatial-analysis-knowledge.json;
 * this module only interprets the graph, it does not encode rules.
 */

import knowledgeBase from './spatial-analysis-knowledge.json';

// ── Operation → data type mapping ─────────────────────────────────────────
// Derived from CLAUDE.md architecture notes and operation-registry.js.
// When the op is missing here, we fall back to summarized_area (the most
// common default in EarthVoice since nearly every dataset ultimately renders
// a choropleth).
const DATA_TYPE_BY_OP = {
  choropleth: 'summarized_area',
  'choropleth-scale': 'summarized_area',
  'choropleth-style': 'summarized_area',
  compare: 'summarized_area',
  'compare-time': 'summarized_area',
  boundaries: 'polygon',
  filter: 'polygon',
  'duckdb-query': 'summarized_area',
  religions: 'polygon',
  languages: 'polygon',
  'climate-zones': 'polygon',
  'fragile-states': 'summarized_area',
  'freedom-house': 'summarized_area',
  'food-security': 'summarized_area',
  faostat: 'summarized_area',
  aquastat: 'summarized_area',
  'who-health': 'summarized_area',
  'wto-trade': 'summarized_area',
  'un-treaties': 'summarized_area',
  'usda-fas': 'summarized_area',
  'h3-density': 'point',
  volcanoes: 'point',
  'heritage-sites': 'point',
  'overlay-cities': 'point',
  'forced-poi': 'point',
  show: 'point',
  'overture-layer': 'point',
  rivers: 'line',
  routing: 'line',
  'render-raster': 'continuous_field',
  terrain: 'continuous_field',
  'physical-features': 'line',
};

// Operations that return POI-like point features with inherently
// categorical attributes (hospital vs airport vs cafe, etc.). These need
// data_type=point and attribute_type=nominal to take the category_map
// path through the decision graph.
const POI_OPS = new Set([
  'forced-poi',
  'overlay-cities',
  'heritage-sites',
  'volcanoes',
  'overture-layer',
]);

// Viz ops — when a query with a "relationship" phrase targets one of
// these, the user wants a bivariate MAP, not a statistical correlation
// test (Issue 5).
const VIZ_OPS = new Set([
  'choropleth',
  'compare',
  'choropleth-style',
  'choropleth-scale',
]);

// Regex tests for intent shape signals.
const RELATIONSHIP_RE = /relationship between|correlation between|\bversus\b|\s+vs\s+|\s+vs\.\s+/i;
const EXPLICIT_DENSITY_VIZ_RE = /density surface|heatmap|kernel density|hexagonal density|hex density/i;

// Ops whose attributes are inherently categorical (no order).
const NOMINAL_OPS = new Set([
  'religions',
  'languages',
  'climate-zones',
  'overture-layer',
  'forced-poi',
  'overlay-cities',
  'heritage-sites',
  'volcanoes',
]);

// Substrings in metric names that signal nominal data.
const NOMINAL_METRIC_KEYWORDS = [
  'religion',
  'language',
  'climate',
  'category',
  'type',
  'zone',
  'class',
];

// Substrings in metric names that signal ordinal data (ranks/scores).
const ORDINAL_METRIC_KEYWORDS = ['rank', 'score', 'tier', 'grade', 'level'];

// ── Goal keyword overrides ────────────────────────────────────────────────
// High-priority phrase → goal mappings. Checked BEFORE the generic
// trigger-phrase loop so that specific analytical method names (PCA,
// DBSCAN, GWR, …) win over loose trigger phrases like "cluster". These
// let coverage queries reliably reach the untraveled paths in the
// decision graph without depending on the ordering of the knowledge
// base's trigger_phrases arrays.
const GOAL_KEYWORD_OVERRIDES = [
  // Cluster-method names FIRST so "local moran" wins over the generic
  // "moran" pattern under identify_patterns, and the nearest+cost-distance
  // combo lands in find_nearby before the model_path "overland" catch.
  { pattern: /local moran|\blisa\b|\bdbscan\b|\bhdbscan\b|\boptics\b|emerging hot|density-?based clustering|hierarchical density/i, goal: 'identify_clusters' },
  { pattern: /\bnearest\b.*(cost distance|overland cost)|(cost distance|overland cost).*nearest/i, goal: 'find_nearby' },
  { pattern: /\bpca\b|principal component|reduce dimension|composite (development )?index|factor analysis|dimension reduction/i, goal: 'multivariate' },
  { pattern: /\bgeodemographic\b|\btypology\b|k-?means (clustering|spatial)|regionalization/i, goal: 'multivariate' },
  { pattern: /\bmoran'?s?\s*i\b|\bautocorrelation\b|join\s+count|k-?function|multi-?scale|\bripley\b|\bquadrat\b|nearest neighbor index/i, goal: 'identify_patterns' },
  { pattern: /\bgwr\b|geographically weighted|spatial lag|spatial error|ols regression|regression model|principal component regression/i, goal: 'analyze_relationships' },
  { pattern: /mean center|median center|standard distance|deviational ellipse|directional mean|geographic center/i, goal: 'measure_distribution' },
  { pattern: /\bsuitab(le|ility)\b|site selection|optimal location|best location for/i, goal: 'find_suitable' },
  { pattern: /location[- ]allocation|\bhuff\b|gravity model|trade area|visitation probability|catchment area/i, goal: 'model_interaction' },
  { pattern: /\bfleet\b|delivery routes?|multi-?stop|vehicle routing|\btrucks\b/i, goal: 'model_path' },
  { pattern: /\boverland\b|cost path|wildlife corridor|pipeline corridor|least-?cost path/i, goal: 'model_path' },
  { pattern: /proportional (circles?|symbols?)|exact (population|value|number) of each/i, goal: 'map_quantities' },
  { pattern: /time series map|tracking map|track the path|hurricane track|side by side/i, goal: 'map_change' },
  { pattern: /utility network|network trace|contamination|trace upstream|trace downstream/i, goal: 'model_flow' },
];

// ── Sub-path inference regexes ────────────────────────────────────────────
// Each pair below maps a regex to a decision-graph edge condition. These
// refine the inferred properties (distribution_aspect, movement_type,
// suitability_logic, …) so the graph walk reaches the intended method.
const SUITABILITY_LOGIC_RULES = [
  { pattern: /\bfuzzy\b|gradually|fuzzy membership|gradual transition/i, value: 'gradual_transition' },
  { pattern: /\bweighted\b|weight(ing)? highest|importance|rank each/i, value: 'weighted_rating' },
];

const PATTERN_DETAIL_RULES = [
  { pattern: /k-?function|multi-?scale|\bripley\b/i, value: 'multi_scale' },
  { pattern: /\bquadrat\b|grid cells?|grid-?based/i, value: 'grid_based' },
];

const DISTRIBUTION_ASPECT_RULES = [
  { pattern: /median center/i, value: 'center_robust' },
  { pattern: /standard distance|spread(.|\s)+(global|out)/i, value: 'spread' },
  { pattern: /deviational ellipse|directional trend/i, value: 'direction' },
  { pattern: /directional mean|mean direction|orientation of/i, value: 'line_orientation' },
  { pattern: /mean center|geographic center/i, value: 'center' },
];

const MOVEMENT_TYPE_RULES = [
  { pattern: /\bfleet\b|delivery routes?|multi-?stop|vehicle routing|\btrucks\b/i, value: 'network_fleet' },
  { pattern: /\boverland\b|wildlife corridor|cost path|pipeline corridor|terrain cost/i, value: 'overland' },
];

const INTERACTION_GOAL_RULES = [
  { pattern: /\bhuff\b|gravity model|trade area|visitation probability|catchment/i, value: 'predict_visitation' },
];

const MULTIVARIATE_GOAL_RULES = [
  { pattern: /\bclassify\b|\btypology\b|geodemographic|k-?means|cluster countries|cluster.*regions/i, value: 'classify_areas' },
  { pattern: /\bpca\b|principal component|reduce dimension|composite|factor analysis/i, value: 'reduce_dimensions' },
];

const DENSITY_UNIFORMITY_RULES = [
  { pattern: /\bhdbscan\b|hierarchical density|varying density/i, value: 'varying' },
  { pattern: /\boptics\b|reachability|explore clustering/i, value: 'exploratory' },
  { pattern: /\bdbscan\b|density-?based clustering/i, value: 'uniform' },
];

const CLUSTER_GOAL_RULES = [
  { pattern: /local moran|\blisa\b|cluster and outlier|spatial outlier/i, value: 'cluster_outlier' },
  { pattern: /emerging|growing|shrinking|temporal hot/i, value: 'temporal' },
];

const RELATIONSHIP_LEVEL_RULES = [
  { pattern: /\bgwr\b|geographically weighted|local regression|varies spatially/i, value: 'prediction_local' },
  { pattern: /spatial lag|spillover|neighboring.*affect/i, value: 'spillover' },
  { pattern: /spatial error|residuals.*autocorrelated|unmeasured spatial/i, value: 'error_dependence' },
  { pattern: /local variation|local bivariate|entropy/i, value: 'local_variation' },
  { pattern: /regression|predict|\bols\b/i, value: 'prediction_global' },
];

const CHANGE_ASPECT_RULES = [
  { pattern: /time series|side by side|year after year|over (the )?decades?/i, value: 'temporal_sequence' },
  { pattern: /\btrack\b|movement|path of|trajectory|hurricane track/i, value: 'movement' },
];

const VIS_PREFERENCE_RULES = [
  { pattern: /contour|isoline/i, value: 'rate_of_change' },
  { pattern: /\b3-?d\b|extrusion|extrude/i, value: '3d' },
];

const DISTANCE_TYPE_RULES = [
  { pattern: /network travel|along (the )?road network|along the network/i, value: 'network' },
  { pattern: /overland cost|cost distance|cost surface/i, value: 'surface' },
  { pattern: /buffer/i, value: 'buffer' },
];

const FLOW_TYPE_RULES = [
  { pattern: /utility network|network trace|contamination|pipe network/i, value: 'utility_network' },
];

/**
 * Apply a list of regex → value rules against a lowercased query.
 * Returns the first matching value, or null.
 */
function matchRules(query, rules) {
  for (const rule of rules) {
    if (rule.pattern.test(query)) return rule.value;
  }
  return null;
}

/**
 * Normalize an intent query to lower-case for substring trigger matching.
 */
function normalizeQuery(intent) {
  const q =
    intent.query ||
    intent.utterance ||
    intent.text ||
    intent.transcript ||
    '';
  return String(q).toLowerCase();
}

/**
 * Map the intent's operation + query to a canonical analysis_goal id.
 * Strategy: first try substring matches against each goal's trigger_phrases
 * (most specific); if none match, fall back to ev_ops membership; if that
 * also fails, default to map_where.
 *
 * Post-match adjustments:
 *   Issue 2 — "density" keyword does not automatically mean map_density.
 *     On polygon/summarized_area data, a "density" metric is just a ratio
 *     attribute (population density, physician density) and should be
 *     mapped as map_quantities, not routed through density_data_check.
 *   Issue 5 — analyze_relationships under a visualization op indicates
 *     the user wants a bivariate map, not a correlation test — downgrade
 *     to map_where so polygon_vis_choice can pick bivariate_choropleth.
 */
function inferAnalysisGoal(intent, dataType) {
  const query = normalizeQuery(intent);
  const op = intent.operation;

  // Priority override — specific analytical method names (PCA, GWR,
  // DBSCAN, …) beat loose trigger phrases like "cluster" that would
  // otherwise route them to the wrong goal. When an override fires the
  // Issue 5 viz-op downgrade is intentionally bypassed because the user
  // named a specific method, not a generic relationship phrase.
  if (query) {
    for (const { pattern, goal } of GOAL_KEYWORD_OVERRIDES) {
      if (pattern.test(query)) {
        return { id: goal, via: 'override_keyword' };
      }
    }
  }

  if (query) {
    for (const goal of knowledgeBase.analysis_goals) {
      const phrases = goal.trigger_phrases || [];
      for (const phrase of phrases) {
        if (query.includes(String(phrase).toLowerCase())) {
          let matched = goal.id;

          if (matched === 'map_density') {
            const explicitDensityViz = EXPLICIT_DENSITY_VIZ_RE.test(query);
            const isPointData = dataType === 'point' || op === 'h3-density';
            if (!isPointData && !explicitDensityViz) {
              matched = 'map_quantities';
            }
          }

          if (matched === 'analyze_relationships' && VIZ_OPS.has(op)) {
            matched = 'map_where';
          }

          return { id: matched, via: 'trigger_phrase' };
        }
      }
    }
  }

  if (op) {
    for (const goal of knowledgeBase.analysis_goals) {
      const ops = goal.ev_ops || [];
      if (ops.includes(op)) {
        return { id: goal.id, via: 'ev_op' };
      }
    }
  }

  return { id: 'map_where', via: 'default' };
}

/**
 * Infer the data type from the operation or explicit intent fields.
 * POI operations always produce point features.
 */
function inferDataType(intent) {
  if (intent.data_type) return { value: intent.data_type, via: 'explicit' };
  if (POI_OPS.has(intent.operation)) return { value: 'point', via: 'poi_op' };
  const mapped = DATA_TYPE_BY_OP[intent.operation];
  if (mapped) return { value: mapped, via: 'op' };
  return { value: 'summarized_area', via: 'default' };
}

/**
 * Infer attribute type from the metric name and operation context.
 * Metric takes precedence over POI nominal-by-op — a numeric metric on a
 * POI op (e.g. "population of each capital city") should be interval_ratio
 * so the graph routes through the quantity path, not the category path.
 */
function inferAttributeType(intent) {
  if (intent.attribute_type) {
    return { value: intent.attribute_type, via: 'explicit' };
  }
  const metric = String(intent.metric || '').toLowerCase();
  if (metric) {
    for (const kw of NOMINAL_METRIC_KEYWORDS) {
      if (metric.includes(kw)) return { value: 'nominal', via: 'metric' };
    }
    for (const kw of ORDINAL_METRIC_KEYWORDS) {
      if (metric.includes(kw)) return { value: 'ordinal', via: 'metric' };
    }
    return { value: 'interval_ratio', via: 'metric' };
  }
  if (NOMINAL_OPS.has(intent.operation)) {
    return { value: 'nominal', via: 'op' };
  }
  return { value: 'interval_ratio', via: 'default' };
}

/**
 * Infer how many variables are being compared. Bivariate when the intent
 * explicitly says so, two metrics are present, or the query contains a
 * relationship phrase ("X versus Y", "relationship between A and B").
 */
function inferVariableCount(intent) {
  if (intent.mode === 'bivariate') return { value: 'two', via: 'mode' };
  if (Array.isArray(intent.metrics) && intent.metrics.length >= 2) {
    return { value: 'two', via: 'metrics' };
  }
  if (intent.metric2 || intent.second_metric) {
    return { value: 'two', via: 'second_metric' };
  }
  const q = normalizeQuery(intent);
  if (q && RELATIONSHIP_RE.test(q) && VIZ_OPS.has(intent.operation)) {
    return { value: 'two', via: 'relationship_phrase' };
  }
  return { value: 'one', via: 'default' };
}

/**
 * Infer the feature-count class — "few" for a handful of specified
 * countries, "many" when the scope is global.
 */
function inferFeatureCountClass(intent) {
  const q = normalizeQuery(intent);
  if (q && /\bproportional (circles?|symbols?)\b|exact (population|value) of each/i.test(q)) {
    return { value: 'few', via: 'proportional_phrase' };
  }
  const countries = intent.countries || intent.isoList || intent.iso3_list;
  if (Array.isArray(countries)) {
    if (countries.length === 0) return { value: 'many', via: 'empty' };
    if (countries.length < 10) return { value: 'few', via: 'list' };
    return { value: 'many', via: 'list' };
  }
  return { value: 'many', via: 'default' };
}

/**
 * Infer the change aspect from a map_change query.
 */
function inferChangeAspect(intent) {
  const q = normalizeQuery(intent);
  return matchRules(q, CHANGE_ASPECT_RULES) || 'magnitude';
}

/**
 * Infer movement type for model_path queries. Default to network_simple
 * since EarthVoice's routing op handles road networks.
 */
function inferMovementType(intent) {
  const q = normalizeQuery(intent);
  return matchRules(q, MOVEMENT_TYPE_RULES) || 'network_simple';
}

/**
 * Infer the density aspect — used by the density_data_check node.
 */
function inferDensityDataType(intent) {
  const dt = inferDataType(intent).value;
  // The graph edges for density_data_check only accept point | polygon |
  // summarized_area, so collapse continuous_field/line onto point for the
  // density surface path.
  if (dt === 'continuous_field' || dt === 'line') return 'point';
  return dt;
}

/**
 * Build the full inferred-property bag consumed by the decision graph.
 *
 * Issue 4: navigate ops are camera/viewport commands, not analysis. We
 *   short-circuit to safe defaults so the secondary clause of a navigate
 *   query ("...show the provinces by population density...") can't hijack
 *   the goal inference.
 * Issue 6: rivers/physical-features default the flow_type to
 *   display_rivers so the graph routes through display_linear_features
 *   instead of stream_delineation.
 */
export function inferSpatialProperties(intent) {
  if (intent && intent.operation === 'navigate') {
    return {
      analysis_goal: 'map_where',
      data_type: 'polygon',
      attribute_type: 'interval_ratio',
      variable_count: 'one',
      feature_count_class: 'many',
      vis_preference: 'default',
      distance_type: 'straight_line',
      change_aspect: 'magnitude',
      distribution_aspect: 'center',
      pattern_detail: 'quick_test',
      density_uniformity: 'uniform',
      cluster_goal: 'hot_cold_spots',
      relationship_level: 'correlation',
      suitability_logic: 'weighted_rating',
      movement_type: 'network_simple',
      flow_type: 'surface_water',
      interaction_goal: 'allocate_demand',
      multivariate_goal: 'classify_areas',
      _provenance: {
        goal: 'navigate',
        data_type: 'navigate',
        attribute_type: 'navigate',
        variable_count: 'navigate',
        feature_count: 'navigate',
      },
    };
  }

  const dataType = inferDataType(intent);
  const goal = inferAnalysisGoal(intent, dataType.value);
  const attributeType = inferAttributeType(intent);
  const variableCount = inferVariableCount(intent);
  const featureCount = inferFeatureCountClass(intent);
  const q = normalizeQuery(intent);

  let flowType = intent.flow_type || matchRules(q, FLOW_TYPE_RULES) || 'surface_water';
  if (
    (intent.operation === 'rivers' || intent.operation === 'physical-features') &&
    flowType === 'surface_water'
  ) {
    flowType = 'display_rivers';
  }

  return {
    analysis_goal: goal.id,
    data_type: dataType.value,
    attribute_type: attributeType.value,
    variable_count: variableCount.value,
    feature_count_class: featureCount.value,
    vis_preference: intent.vis_preference || matchRules(q, VIS_PREFERENCE_RULES) || 'default',
    distance_type: intent.distance_type || matchRules(q, DISTANCE_TYPE_RULES) || 'straight_line',
    change_aspect: inferChangeAspect(intent),
    distribution_aspect: intent.distribution_aspect || matchRules(q, DISTRIBUTION_ASPECT_RULES) || 'center',
    pattern_detail: intent.pattern_detail || matchRules(q, PATTERN_DETAIL_RULES) || 'quick_test',
    density_uniformity: intent.density_uniformity || matchRules(q, DENSITY_UNIFORMITY_RULES) || 'uniform',
    cluster_goal: intent.cluster_goal || matchRules(q, CLUSTER_GOAL_RULES) || 'hot_cold_spots',
    relationship_level: intent.relationship_level || matchRules(q, RELATIONSHIP_LEVEL_RULES) || 'correlation',
    suitability_logic: intent.suitability_logic || matchRules(q, SUITABILITY_LOGIC_RULES) || 'strict_binary',
    movement_type: inferMovementType(intent),
    flow_type: flowType,
    interaction_goal: intent.interaction_goal || matchRules(q, INTERACTION_GOAL_RULES) || 'allocate_demand',
    multivariate_goal: intent.multivariate_goal || matchRules(q, MULTIVARIATE_GOAL_RULES) || 'reduce_dimensions',
    // Provenance used for confidence scoring — not consumed by the graph.
    _provenance: {
      goal: goal.via,
      data_type: dataType.via,
      attribute_type: attributeType.via,
      variable_count: variableCount.via,
      feature_count: featureCount.via,
    },
  };
}

/**
 * Walk the decision graph starting at `start`, following edges whose
 * condition equals the property in `props` referenced by the node's
 * match_field. Returns { methodId, path }.
 *
 * When the density path needs data_type, we fall back to a collapsed
 * version (see inferDensityDataType).
 */
function walkGraph(props) {
  const path = [];
  let cursor = 'start';
  let guard = 0;

  while (cursor && guard < 32) {
    guard += 1;
    path.push(cursor);
    const node = knowledgeBase.decision_graph[cursor];
    if (!node) break;
    if (node.recommend && !node.edges) {
      return { methodId: node.recommend, path };
    }
    const matchField = node.match_field;
    let value = props[matchField];

    // Density node reuses data_type but collapses continuous/line → point.
    if (cursor === 'density_data_check') {
      value = inferDensityDataType({ operation: null, data_type: value });
    }

    const edges = node.edges || [];
    let edge = edges.find((e) => e.condition === value);
    // Fall back to first edge when no match — keeps the advisor robust
    // against unfamiliar intent shapes.
    if (!edge && edges.length > 0) edge = edges[0];
    if (!edge) break;
    if (edge.recommend) return { methodId: edge.recommend, path };
    if (edge.next) {
      cursor = edge.next;
      continue;
    }
    break;
  }

  return { methodId: 'graduated_colors', path };
}

/**
 * Given a data profile from the classification-engine profiler, pick the
 * best classification scheme. See scheme-selection rules in the textbook
 * knowledge base (classification_schemes section).
 */
function pickClassificationFromProfile(profile) {
  if (!profile) return null;
  const skew = Math.abs(Number(profile.skewness) || 0);
  const kurt = Number(profile.kurtosis);

  if (skew > 2) return 'log-quantile';
  if (Number.isFinite(kurt) && kurt < 2) return 'jenks';
  if (
    Number.isFinite(kurt) &&
    skew < 0.5 &&
    Math.abs(kurt - 3) < 1.5
  ) {
    return 'standard-deviation';
  }
  return null;
}

/**
 * Pick an appropriate color ramp family from the method and inferred
 * attribute type.
 */
function pickColorRampType(method, props) {
  if (method.ev_classification === 'bivariate' || props.variable_count === 'two') {
    return 'bivariate';
  }
  if (props.attribute_type === 'nominal') return 'qualitative';
  if (method.category === 'change_analysis') return 'diverging';
  if (method.category === 'cluster_detection') return 'diverging';
  if (method.ev_classification === 'standard-deviation') return 'diverging';
  return 'sequential';
}

/**
 * Find up to 2 alternative methods in the same category (excluding the
 * chosen method itself). Cheap list so the UI can show "you could also try
 * X or Y" hints.
 */
function findAlternatives(methodId) {
  const chosen = knowledgeBase.methods[methodId];
  if (!chosen) return [];
  const category = chosen.category;
  const alts = [];
  for (const [id, m] of Object.entries(knowledgeBase.methods)) {
    if (id === '_note' || id === methodId) continue;
    if (m && m.category === category) {
      alts.push({ id, label: m.label, ev_op: m.ev_op });
      if (alts.length >= 2) break;
    }
  }
  return alts;
}

/**
 * Compute a confidence score in [0,1] from the provenance of inferred
 * properties.
 */
function scoreConfidence(props) {
  const prov = props._provenance || {};
  const explicit = (v) =>
    v === 'explicit' ||
    v === 'mode' ||
    v === 'metrics' ||
    v === 'second_metric' ||
    v === 'relationship_phrase' ||
    v === 'navigate' ||
    v === 'override_keyword' ||
    v === 'proportional_phrase';
  const derived = (v) =>
    v === 'op' ||
    v === 'poi_op' ||
    v === 'metric' ||
    v === 'trigger_phrase' ||
    v === 'ev_op' ||
    v === 'list';
  let score = 0;
  let count = 0;
  for (const v of Object.values(prov)) {
    count += 1;
    if (explicit(v)) score += 1.0;
    else if (derived(v)) score += 0.95;
    else score += 0.7; // default / fallback
  }
  return count > 0 ? score / count : 0.5;
}

/**
 * Walk the decision graph and return an enrichment recommendation.
 *
 * @param {object} intent - parsed intent JSON from the DIP
 * @param {object} [dataProfile] - optional data profile from the
 *   classification engine (skewness, kurtosis, min, max, count, nullCount).
 * @returns {object} enrichment
 */
export function adviseSpatialMethod(intent, dataProfile = null) {
  try {
    const safeIntent = intent && typeof intent === 'object' ? intent : {};
    const props = inferSpatialProperties(safeIntent);
    const { methodId, path } = walkGraph(props);
    const method = knowledgeBase.methods[methodId];

    if (!method) {
      return defaultEnrichment('method-not-found', path);
    }

    // Classification: advisor's default comes from the method, then is
    // refined by the data profile when one is available.
    let classification = method.ev_classification || 'auto';
    const profileClassification = pickClassificationFromProfile(dataProfile);
    if (profileClassification) {
      classification = profileClassification;
    }

    const colorRampType = pickColorRampType(method, props);
    const alternatives = findAlternatives(methodId);
    let confidence = scoreConfidence(props);

    // Penalize when the op was entirely unknown (couldn't infer data_type
    // from the op map) — these recommendations are essentially fallbacks.
    if (props._provenance && props._provenance.data_type === 'default') {
      confidence = Math.min(confidence, 0.6);
    }

    // Issue 1: ev_op is a pass-through of the intent's operation. The
    // DIP already picked the right handler; the advisor only recommends
    // HOW to visualize (method, layer type, classification, ramp). The
    // method's ev_op is only used as a fallback when the intent has no
    // operation or it's the literal 'unknown' sentinel.
    const intentOp = safeIntent.operation;
    const effectiveEvOp =
      intentOp && intentOp !== 'unknown' ? intentOp : method.ev_op || null;

    // For point data, methods whose default ev_layer_type is 'fill'
    // (e.g. category_map, which is polygon-oriented in the knowledge
    // base) should render as 'circle' — points don't fill.
    let effectiveLayerType = method.ev_layer_type || null;
    if (props.data_type === 'point' && effectiveLayerType === 'fill') {
      effectiveLayerType = 'circle';
    }

    const inferredSnapshot = {
      analysis_goal: props.analysis_goal,
      data_type: props.data_type,
      attribute_type: props.attribute_type,
      variable_count: props.variable_count,
      feature_count_class: props.feature_count_class,
    };

    const enrichment = {
      method: methodId,
      label: method.label,
      description: method.description,
      when_to_use: method.when_to_use || '',
      ev_op: effectiveEvOp,
      ev_layer_type: effectiveLayerType,
      ev_classification: classification,
      color_ramp_type: colorRampType,
      confidence,
      alternatives,
      pitfalls: Array.isArray(method.pitfalls) ? method.pitfalls.slice() : [],
      decision_path: path,
      source_texts: Array.isArray(method.sources) ? method.sources.slice() : [],
      inferred: inferredSnapshot,
      // Alias consumed by the Spatial Insights UI card.
      _inferred: inferredSnapshot,
    };

    if (typeof console !== 'undefined' && console.debug) {
      console.debug(
        `[spatial-advisor] ${methodId} (conf=${confidence.toFixed(2)}) path=${path.join('→')}`
      );
    }

    return enrichment;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[spatial-advisor] failed to resolve method:', err);
    }
    return defaultEnrichment('exception', ['start']);
  }
}

/**
 * Default (confidence=0) enrichment used when the advisor can't resolve
 * anything. Downstream consumers should treat this as a no-op hint.
 */
function defaultEnrichment(reason, path) {
  return {
    method: 'graduated_colors',
    label: 'Graduated colors (fallback)',
    description: `Advisor fallback — ${reason}. Using graduated colors as a safe default.`,
    ev_op: 'choropleth',
    ev_layer_type: 'fill',
    ev_classification: 'auto',
    color_ramp_type: 'sequential',
    confidence: 0,
    alternatives: [],
    pitfalls: [],
    decision_path: path || ['start'],
    source_texts: [],
    inferred: {},
  };
}

export default { adviseSpatialMethod, inferSpatialProperties };
